/** Signature-stage rich rendering — HF EU prompt schema.
 *
 * Adds a prominent "manual verification" callout when the agent reports
 * NOT_EXECUTED: PyMuPDF extracts TEXT, so wet-ink (handwritten) signatures
 * on a scanned page are invisible to it. The reviewer should always
 * cross-check against the PDF viewer on the right column before treating
 * the agent's "not signed" verdict as authoritative.
 */
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, FileSignature, Eye } from 'lucide-react';
import { ConfidenceBadge } from './ConfidenceBadge';

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asText = (v: unknown): string => (typeof v === 'string' ? v : '');
const asBool = (v: unknown): boolean => Boolean(v);
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

const SEVERITY_CLS: Record<string, string> = {
  LOW: 'bg-green-50 text-green-700 border-green-200',
  MEDIUM: 'bg-amber-50 text-amber-800 border-amber-200',
  HIGH: 'bg-orange-50 text-orange-800 border-orange-300',
  CRITICAL: 'bg-red-50 text-red-800 border-red-300',
};

const STATUS_CLS: Record<string, string> = {
  FULLY_EXECUTED: 'bg-green-50 text-green-700 border-green-200',
  PARTIALLY_EXECUTED: 'bg-amber-50 text-amber-800 border-amber-200',
  NOT_EXECUTED: 'bg-red-50 text-red-800 border-red-300',
  EXECUTION_DEFECTS: 'bg-orange-50 text-orange-800 border-orange-300',
};

function YesNo({ label, value }: { label: string; value: unknown }) {
  const tone = value === true
    ? 'text-green-700 dark:text-green-400'
    : value === false
      ? 'text-red-700 dark:text-red-400'
      : 'text-muted-foreground';
  const display = value === true ? 'yes' : value === false ? 'no' : '—';
  return (
    <div className="flex justify-between gap-3 text-xs border-b py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium ${tone}`}>{display}</span>
    </div>
  );
}

function SignatureBlockCard({ block }: { block: Record<string, unknown> }) {
  const found = asBool(block.signature_found);
  const issues = asArray(block.issues).map((x) => (typeof x === 'string' ? x : ''));
  return (
    <Card className="border-border">
      <CardContent className="pt-3 pb-3 px-4 space-y-1 text-xs">
        <div className="flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="font-medium">{asText(block.party_legal_name) || '—'}</span>
          {asText(block.party_id) && (
            <Badge variant="outline" className="text-[10px]">{asText(block.party_id)}</Badge>
          )}
          <Badge
            variant="outline"
            className={`text-[10px] ml-auto ${found ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}
          >
            {found ? 'Signature found' : 'Not found'}
          </Badge>
        </div>
        {found ? (
          <div className="space-y-0.5">
            {asText(block.signatory_name_in_block) && (
              <div><span className="text-muted-foreground">Signatory: </span>{asText(block.signatory_name_in_block)}</div>
            )}
            {asText(block.signatory_title) && (
              <div><span className="text-muted-foreground">Title: </span>{asText(block.signatory_title)}</div>
            )}
            {asText(block.signing_date) && (
              <div><span className="text-muted-foreground">Date: </span>{asText(block.signing_date)} ({asText(block.signing_date_format) || '—'})</div>
            )}
            {asText(block.signature_type) && (
              <div><span className="text-muted-foreground">Type: </span>{asText(block.signature_type)}</div>
            )}
            {asText(block.authority_basis) && (
              <div><span className="text-muted-foreground">Authority: </span>{asText(block.authority_basis)}</div>
            )}
          </div>
        ) : null}
        {issues.length > 0 && (
          <ul className="list-disc list-inside text-red-700 dark:text-red-300">
            {issues.map((iss, i) => <li key={i}>{iss}</li>)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function RichSignatureBody({ output }: { output: Record<string, unknown> }) {
  const blocks = asArray(output.signature_blocks);
  const dateVal = obj(output.date_validation);
  const exec = obj(output.execution_completeness);
  const formal = obj(output.jurisdiction_formalities);
  const esig = obj(output.electronic_signature_assessment);
  const status = asText(output.overall_signature_status) || 'NOT_EXECUTED';
  const statusCls = STATUS_CLS[status] ?? '';
  const summary = asText(output.signature_summary);
  const flags = asArray(output.flags_for_downstream).map((f) => (typeof f === 'string' ? f : ''));
  const notes = asText(output.extraction_notes);
  const dateIssues = asArray(dateVal.date_inconsistencies);
  const confidence = output.signature_confidence_score;
  const allFalse = blocks.every((b) => !asBool(obj(b).signature_found));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className={`text-xs ${statusCls}`}>{status}</Badge>
        <span className="ml-auto">
          <ConfidenceBadge score={confidence} rationale={notes} />
        </span>
      </div>

      {/* Wet-ink fallback callout — PyMuPDF can't see handwritten signatures
          on scanned pages. When the agent says NOT_EXECUTED but the PDF
          might still be signed in ink, the reviewer must verify by eye. */}
      {status === 'NOT_EXECUTED' && allFalse && (
        <Alert>
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden="true" />
          <AlertDescription className="text-sm">
            <span className="font-medium flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" aria-hidden="true" />
              Verify against the PDF before approving
            </span>
            <span className="text-xs text-muted-foreground block mt-1">
              The Signature agent extracts text only. Handwritten (wet-ink) signatures
              on scanned pages won't be detected — the contract may still be validly
              executed. Open the PDF on the right and confirm visually. If signed,
              use <span className="font-medium">Edit before approve</span> to set
              the relevant signature_blocks fields, then approve. If genuinely
              unsigned, approve as-is and Gate 2 will flag it.
            </span>
          </AlertDescription>
        </Alert>
      )}

      {summary && (
        <div className="rounded border bg-muted/30 p-3 text-sm">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Execution summary
          </p>
          <p>{summary}</p>
        </div>
      )}

      <div className="space-y-2">
        {blocks.map((b, i) => <SignatureBlockCard key={i} block={obj(b)} />)}
      </div>

      <Card className="border-border">
        <CardContent className="pt-3 pb-3 px-4 space-y-1 text-xs">
          <div className="font-medium text-sm mb-1">Date validation</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
            <div><span className="text-muted-foreground">Effective: </span>{asText(dateVal.effective_date) || '—'}</div>
            <div><span className="text-muted-foreground">Start: </span>{asText(dateVal.contract_start_date) || '—'}</div>
            <div><span className="text-muted-foreground">End: </span>{asText(dateVal.contract_end_date) || '—'}</div>
            <div><span className="text-muted-foreground">Signing dates: </span>{asArray(dateVal.signing_dates_found).filter((d): d is string => typeof d === 'string').join(', ') || '—'}</div>
            <YesNo label="Signing before effective" value={dateVal.signing_before_effective} />
            <YesNo label="All parties same date" value={dateVal.all_parties_signed_same_date} />
          </div>
          {dateIssues.length > 0 && (
            <ul className="space-y-0.5 mt-1">
              {dateIssues.map((d, i) => {
                const o = obj(d);
                const sev = asText(o.severity) || 'MEDIUM';
                return (
                  <li key={i} className="flex items-start gap-2">
                    <Badge variant="outline" className={`text-[10px] shrink-0 ${SEVERITY_CLS[sev] ?? ''}`}>
                      {sev}
                    </Badge>
                    <span>{asText(o.description) || '—'}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Card className="border-border">
          <CardContent className="pt-3 pb-3 px-4 text-xs space-y-0.5">
            <div className="font-medium text-sm mb-1">Execution completeness</div>
            <YesNo label="All parties signed" value={exec.all_parties_signed} />
            <YesNo label="Counterpart clause" value={exec.counterpart_clause_present} />
            <YesNo label="Entire-agreement clause" value={exec.entire_agreement_clause_present} />
            <YesNo label="Amendments require consent" value={exec.amendments_require_written_consent} />
            {asArray(exec.unsigned_party_ids).length > 0 && (
              <div>
                <span className="text-muted-foreground">Unsigned: </span>
                {asArray(exec.unsigned_party_ids).filter((p): p is string => typeof p === 'string').join(', ')}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-3 pb-3 px-4 text-xs space-y-0.5">
            <div className="font-medium text-sm mb-1">Jurisdictional formalities</div>
            <YesNo label="Notarisation required" value={formal.notarisation_required} />
            <YesNo label="Notarisation present" value={formal.notarisation_present} />
            <YesNo label="Witness required by law" value={formal.witness_required_by_law} />
            <YesNo label="Witness requirement met" value={formal.witness_requirement_met} />
            <YesNo label="Registration required" value={formal.registration_required} />
            <YesNo label="Registration mentioned" value={formal.registration_mentioned} />
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardContent className="pt-3 pb-3 px-4 text-xs space-y-0.5">
          <div className="font-medium text-sm mb-1">Electronic signature assessment</div>
          <YesNo label="E-signatures detected" value={esig.electronic_signatures_detected} />
          <YesNo label="eIDAS compliant" value={esig.eidas_compliant} />
          <YesNo label="E-sig clause present" value={esig.electronic_signature_clause_present} />
          {asText(esig.risk_note) && (
            <p className="italic text-muted-foreground border-l-2 pl-2 mt-1">
              Risk note: {asText(esig.risk_note)}
            </p>
          )}
        </CardContent>
      </Card>

      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {flags.map((f, i) => (
            <span
              key={i}
              className="text-[10px] rounded bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 px-1.5 py-0.5"
            >
              {f}
            </span>
          ))}
        </div>
      )}

      {notes && (
        <p className="text-xs italic text-muted-foreground border-l-2 pl-2">
          Extraction notes: {notes}
        </p>
      )}
    </div>
  );
}
